import BigNumber from 'big.js';
import { FIAT_SYMBOLS } from '../constants';
import { BigOrZero, BigWithFormatInstance } from './big';
import { format as formatDateFns, addMinutes } from 'date-fns';

type AmountFormatOptions = {
  average?: boolean;
  decimals?: number;
  preferCurrencyCode?: boolean;
};

type Abbreviation = '' | 'K' | 'M' | 'B' | 'T';

const UNIT_SUFFIXES: [number, Abbreviation][] = [
  [1e12, 'T'],
  [1e9, 'B'],
  [1e6, 'M'],
  [1e3, 'K'],
  [1, ''],
];

const _getAmount = (
  amount: string | BigNumber | undefined | null,
  decimalsOrOptions?: number | AmountFormatOptions
) => {
  const options: AmountFormatOptions = {};

  if (typeof decimalsOrOptions === 'number') {
    options.decimals = decimalsOrOptions;
  } else {
    Object.assign(options, decimalsOrOptions);
  }

  const customDecimals = typeof options.decimals !== 'undefined';

  const value = BigOrZero(amount);
  let formatted: string = '';
  if (options.average) {
    const match = UNIT_SUFFIXES.find(([threshold]) => value.gte(threshold));
    if (match) {
      const [threshold, suffix] = match;
      const shortValue = value.div(threshold);
      formatted = `${(shortValue as BigWithFormatInstance).toFormat(options.decimals)}${suffix}`;
    } else {
      formatted = value.toFormat(options.decimals);
    }
  } else {
    formatted = value.toFormat(options.decimals);
  }

  if (!customDecimals) {
    // TODO(Hadrien): investigate if this is still necessary with numbro gone
    // Note: There is no "minimal decimal places" option in numbro so we have to do it by hand
    const [, formattedDecimals] = formatted.split('.');

    if (!formattedDecimals) {
      formatted += '.00'; // E.g. 1 -> 1.00
    } else if (formattedDecimals.length < 2) {
      formatted += '0'; // E.g. 1.1 -> 1.10
    }
  }

  return formatted;
};

const _getCurrency = (
  amount: string | BigNumber | undefined | null,
  currencyDisplayCode: string,
  options: AmountFormatOptions = {}
) => {
  const formattedAmount = _getAmount(amount, {
    ...options,
    decimals:
      options.average && options.decimals === undefined ? 1 : options.decimals,
  });

  const hasFiatSymbol = currencyDisplayCode in FIAT_SYMBOLS;

  if (hasFiatSymbol && !options.preferCurrencyCode) {
    const fiatSymbol = FIAT_SYMBOLS[currencyDisplayCode];

    if (formattedAmount.startsWith('-')) {
      return `-${fiatSymbol}${formattedAmount.substring(1)}`;
    }

    return `${fiatSymbol}${formattedAmount}`;
  }

  return `${formattedAmount} ${currencyDisplayCode}`;
};

const _forceSign = (value: string, sign: string): string => {
  return `${sign}${value}`;
};

function getBaseCurrency(
  value: string | BigNumber | undefined | null,
  options: AmountFormatOptions = {}
) {
  return _getCurrency(value, 'USD', options);
}

export function getDate(date: Date, format: string, toUtc?: boolean) {
  if (!date) {
    return '';
  }
  if (toUtc) {
    return formatDateFns(addMinutes(date, date.getTimezoneOffset()), format);
  }
  return formatDateFns(date, format);
}

export const formatters = {
  getAmount: _getAmount,
  getBaseCurrency,
  getCurrency: _getCurrency,
  forceSign: _forceSign,
  getDate,
};
